import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Avatar,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { IoCloseCircleOutline } from "react-icons/io5";
import ReactPDF from "react-pdf-js";
import { BUCKET_URL } from "../../../../Service/api/ApiUrls";
import { formatDate } from "../../Masters/Locations/Utils/TableHeadList";

interface TestResultPopupProps {
  open: boolean;
  onClose: () => void;
  data: {
    customerName: string;
    testName: string;
    instruction: string;
    priorityLevel: string;
    updatedOn: string;
    notes: string;
    reportName: string;
    reportPath: string;
    phone: string | number;
    age: number | null;
    Gender: string;
  };
}

const PriorityLabel = styled("span")<{ priority: string }>(({ priority }) => ({
  padding: "5px 10px",
  borderRadius: "10px",
  fontSize: "0.75rem",
  color:
    priority === "Urgent"
      ? "#E2005C"
      : priority === "Routine"
      ? "#007FFF"
      : "#FF3D57",
  backgroundColor:
    priority === "Urgent"
      ? "#E2005C"
      : priority === "Routine"
      ? "#E0F7FF"
      : "#FFE7E9",
  fontWeight: 500,
}));

const TestOrderPopup = ({ open, onClose, data }: any) => {
  const reportPath = data?.reportPath || "";
  const reportName = data?.reportName || "";
  const reportUrl = `${BUCKET_URL}/${reportPath}${reportName}`;
  const isImage = /\.(jpg|jpeg|png|gif|bmp)$/i.test(reportName);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <span style={{ fontWeight: 700 }}>Test Result</span>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <IoCloseCircleOutline />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Patient Info */}
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ fontSize: "0.875rem", marginBottom: 8 }}
        >
          Updated on {formatDate(data?.updatedOn) || "N/A"}
        </Typography>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar style={{ width: 48, height: 48, marginRight: 16 }} />
          <Box>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {data?.customerName || "Unknown"}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ fontSize: "0.875rem", fontWeight: "600" }}
            >
              {data?.phone || "N/A"} {data?.age || "N/A"}{" "}
              {data?.Gender || "N/A"}
            </Typography>
          </Box>
          <Box ml="auto">
            {data?.priorityLevel === "urgent" ? (
              <span className="urgent rounded-pill p-1 ms-2">
                {data?.priorityLevel}
              </span>
            ) : (
              <span className="routine rounded-pill p-1 ms-2">
                {data?.priorityLevel}
              </span>
            )}
          </Box>
        </Box>

        {/* Test Details */}
        <Box mt={2}>
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            Test Name
          </Typography>
          <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
            {data?.testName || "N/A"}
          </Typography>
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, fontSize: "0.875rem", marginTop: "16px" }}
          >
            Instructions
          </Typography>
          <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
            {data?.instruction || "No instructions provided."}
          </Typography>
        </Box>

        <Divider style={{ margin: "16px 0" }} />
        {/* Notes */}
        <Box>
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            Notes
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: "0.875rem" }}
          >
            {data?.notes || "No additional notes provided."}
          </Typography>
        </Box>
        <Divider style={{ margin: "16px 0" }} />
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, fontSize: "0.875rem" }}
        >
          Reports
        </Typography>
        {reportPath && reportName ? (
          <Paper
            variant="outlined"
            style={{
              padding: "16px",
              marginTop: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "300px",
              overflow: "hidden",
              cursor: "pointer", // Indicate that it's clickable
            }}
            onClick={() => window.open(reportUrl, "_blank")} // Open the PDF in a new tab
          >
            {isImage ? (
              <img
                src={reportUrl}
                alt={reportName}
                style={{ maxHeight: "100%", maxWidth: "100%" }}
              />
            ) : (
              <ReactPDF file={reportUrl} page={1} />
            )}
          </Paper>
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: "0.875rem", marginTop: "16px" }}
          >
            No report attached.
          </Typography>
        )}

        <Divider style={{ margin: "16px 0" }} />
      </DialogContent>
    </Dialog>
  );
};

export default TestOrderPopup;
